<template>
    <div class="office-holiday-requests office-holiday-requests__container container-fluid">
        <div class="card">
            <div class="card-body">
                <form
                    class="row office-holiday-requests__search"
                    action=""
                    @submit.prevent="searchRequests"
                >
                    <div class="col-md-2 col-sm-12">
                        <label>User</label>
                        <user-select
                            v-model="params.user_id"
                            :autoselect="false"
                        />
                    </div>
                    <div class="col-md-2 col-sm-12">
                        <label>Status</label>
                        <select
                            class="form-control"
                            v-model="params.status"
                        >
                            <option value="pending">
                                Pending
                            </option>
                            <option value="approved">
                                Approved
                            </option>
                            <option value="declined">
                                Declined
                            </option>
                            <option value="all">
                                All
                            </option>
                        </select>
                    </div>
                    <div class="col-md-2 col-sm-12">
                        <label>From Date</label>
                        <date-picker v-model="params.from_date" />
                    </div>
                    <div class="col-md-2 col-sm-12">
                        <label>To Date</label>
                        <date-picker v-model="params.to_date" />
                    </div>
                    <div class="col-md-4 col-sm-12 text-right">
                        <br>
                        <button
                            class="btn btn-primary"
                            v-if="!loading"
                        >
                            Search
                        </button>
                        <button
                            class="btn btn-primary"
                            v-if="loading"
                            disabled
                        >
                            Loading...
                        </button>
                        <button
                            class="btn btn-default"
                            @click.prevent="clearSearch"
                        >
                            Clear
                        </button>
                    </div>
                </form>
            </div>
        </div>
        <div class="card">
            <div class="card-body">
                <table
                    class="table"
                    v-if="loaded"
                >
                    <thead>
                        <tr>
                            <th>User</th>
                            <th>Start</th>
                            <th>End</th>
                            <th>Days</th>
                            <th>Status</th>
                            <th>Reviewed By</th>
                            <th class="text-right">
                                <select
                                    v-model="params.order"
                                    @change="onListOptionsChange"
                                    class="form-control"
                                    style="width: 12rem; display: inline-block; margin-right: 0.5rem;"
                                >
                                    <option value="start_at_asc">
                                        Start Date (asc)
                                    </option>
                                    <option value="start_at_desc">
                                        Start Date (desc)
                                    </option>
                                </select>
                                <select
                                    v-model="params.per_page"
                                    @change="onListOptionsChange"
                                    class="form-control text-right"
                                    style="width: 10rem; display: inline-block;"
                                >
                                    <option value="25">
                                        25 per page
                                    </option>
                                    <option value="50">
                                        50 per page
                                    </option>
                                    <option value="100">
                                        100 per page
                                    </option>
                                </select>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-if="!loading && !requests.length">
                            <td colspan="7">
                                No holiday requests to show for current filter, try
                                <a
                                    href="#"
                                    @click.prevent="clearSearch"
                                >clearing</a>
                                your filter
                            </td>
                        </tr>
                        <request-row
                            v-for="request in requests"
                            :key="request.id"
                            :request="request"
                            @review="showReviewModal(request)"
                            @deleted="onDeleted"
                        />
                    </tbody>
                </table>

                <pagination
                    :page="params.page"
                    :last-page="lastPage"
                    v-if="loaded"
                    @pageChange="pageChange"
                />
            </div>
        </div>
        <form-modal
            v-if="openEvent"
            :event="openEvent"
            @saved="onSaved"
            @close="openEvent = null"
        />
    </div>
</template>

<script>
import UserSelect from '../../UserSelectComponent';
import DatePicker from '../../DateTimePickerComponent';
import RequestRow from './RowComponent';
import Pagination from '../../PaginationComponent';
import FormModal from '../calendar/FormModal.vue';
import HolidayRequestsService from '../../../services/HolidayRequestsService';
import AuthService from '../../../services/AuthService';

export default {
    components: {
        UserSelect,
        DatePicker,
        RequestRow,
        Pagination,
        FormModal,
    },
    data () {
        return {
            loading: false,
            loaded: false,
            params: {
                page: 1,
                user_id: null,
                status: 'pending',
                per_page: 25,
                order: 'start_at_asc',
                from_date: null,
                to_date: null,
            },
            requests: [],
            total: 0,
            lastPage: 1,
            openEvent: null,
        };
    },
    mounted () {
        if (!AuthService.user || !AuthService.user.admin_access) {
            this.$router.replace('/office/');
            return;
        }
        this.loadRequests();
        // OfficeComponent owns the calendar-events Echo subscription and relays events via $root.
        this.$root.$on('CalendarEventUpdated', this.onCalendarEventUpdated);
        this.$root.$on('CalendarEventDeleted', this.onCalendarEventDeleted);
    },
    beforeDestroy () {
        this.$root.$off('CalendarEventUpdated', this.onCalendarEventUpdated);
        this.$root.$off('CalendarEventDeleted', this.onCalendarEventDeleted);
    },
    methods: {
        searchRequests () {
            this.params.page = 1;
            this.loadRequests();
        },
        onListOptionsChange () {
            this.$nextTick(() => {
                this.searchRequests();
            });
        },
        loadRequests () {
            this.loading = true;
            const params = {...this.params};
            params.from_date = params.from_date ? params.from_date.format('YYYY-MM-DD HH:mm') : null;
            params.to_date = params.to_date ? params.to_date.format('YYYY-MM-DD HH:mm') : null;
            HolidayRequestsService.index(params).then(res => {
                this.requests = res.data.data;
                this.total = res.data.total;
                this.lastPage = res.data.last_page;
                this.loading = false;
                this.loaded = true;
            });
        },
        pageChange (page) {
            this.params.page = page;
            this.loadRequests();
        },
        clearSearch () {
            this.params = {
                page: 1,
                user_id: null,
                status: 'pending',
                from_date: null,
                to_date: null,
                per_page: this.params.per_page || 25,
                order: 'start_at_asc',
            };
            this.loadRequests();
        },
        showReviewModal (request) {
            this.openEvent = {
                ...request,
                start_at: window.moment(request.start_at),
                end_at: window.moment(request.end_at),
                notes: request.description,
            };
        },
        onSaved () {
            this.openEvent = null;
            this.loadRequests();
        },
        onDeleted (request) {
            const i = this.requests.findIndex(item => item.id === request.id);
            if (i >= 0) {
                this.requests.splice(i, 1);
            }
            if (this.openEvent && this.openEvent.id === request.id) {
                this.openEvent = null;
            }
        },
        eventMatchesFilters (event) {
            if (!event || event.type !== 'Annual Leave' || !event.user_id) {
                return false;
            }
            if (this.params.status !== 'all') {
                const statusMap = {
                    pending: 'Pending',
                    approved: 'Approved',
                    declined: 'Declined',
                };
                if (event.status !== statusMap[this.params.status]) {
                    return false;
                }
            }
            if (this.params.user_id && Number(event.user_id) !== Number(this.params.user_id)) {
                return false;
            }
            if (this.params.from_date && window.moment(event.start_at).isBefore(this.params.from_date)) {
                return false;
            }
            if (this.params.to_date && window.moment(event.start_at).isAfter(this.params.to_date)) {
                return false;
            }

            return true;
        },
        onCalendarEventUpdated (event) {
            const calendarEvent = event.calendarEvent;
            if (!calendarEvent) {
                return;
            }
            const i = this.requests.findIndex(request => request.id === calendarEvent.id);
            const matches = this.eventMatchesFilters(calendarEvent);
            if (i >= 0) {
                if (matches) {
                    this.requests.splice(i, 1, calendarEvent);
                } else {
                    this.requests.splice(i, 1);
                }
                this.$forceUpdate();
                return;
            }
            if (matches) {
                this.requests.unshift(calendarEvent);
                this.$forceUpdate();
            }
        },
        deletedEventId (event) {
            if (!event) {
                return null;
            }
            if (event.calendarEvent && event.calendarEvent.id) {
                return event.calendarEvent.id;
            }
            if (event.calendar_event && event.calendar_event.id) {
                return event.calendar_event.id;
            }
            if (event.calendar_event_id) {
                return event.calendar_event_id;
            }

            return null;
        },
        onCalendarEventDeleted (event) {
            const id = this.deletedEventId(event);
            if (!id) {
                return;
            }
            const i = this.requests.findIndex(request => request.id === id);
            if (i >= 0) {
                this.requests.splice(i, 1);
                this.$forceUpdate();
            }
            if (this.openEvent && this.openEvent.id === id) {
                this.openEvent = null;
            }
        },
    },
};
</script>



// WEBPACK FOOTER //
// resources/assets/js/components/office/holiday-requests/IndexComponent.vue